import React, { useState, useEffect } from 'react';
import { fetchCourseFeeDashboard } from '../services/academicFinanceApi';
import Notifier from '../../../Utils/notify';
import { Chart as ChartJS, ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import '../styles/CourseFeeDashboard.css';
import SpinnerLoader from '../../../Utils/Spinner';

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export const CourseFeeDashboard = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCourse, setExpandedCourse] = useState(null);
  const [activeTab, setActiveTab] = useState('imposed'); // imposed, scheduled, ledger

  useEffect(() => {
    loadCourseFeeDashboard();
  }, []);

  const loadCourseFeeDashboard = async () => {
    try {
      setLoading(true);
      const data = await fetchCourseFeeDashboard();
      setCourses(data.courses || []);
    } catch (error) {
      console.error('Error loading course fee dashboard:', error);
      Notifier.error('Failed to load course fee dashboard');
    } finally {
      setLoading(false);
    }
  };

  const toggleCourse = (courseId) => {
    setExpandedCourse(expandedCourse === courseId ? null : courseId);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercentage = (value) => {
    return `${value.toFixed(2)}%`;
  };

  const renderAnalytics = (analytics) => {
    if (!analytics) return null;

    return (
      <div className="analytics-section">
        <h4>Analytics Overview</h4>
        <div className="analytics-grid">
          <div className="analytics-card totals">
            <h5>Financial Totals</h5>
            <div className="analytics-items">
              <div className="analytics-item">
                <span className="label">Total Imposed:</span>
                <span className="value">{formatCurrency(analytics.totals.imposed)}</span>
              </div>
              <div className="analytics-item">
                <span className="label">Total Scheduled:</span>
                <span className="value">{formatCurrency(analytics.totals.scheduled)}</span>
              </div>
              <div className="analytics-item">
                <span className="label">Total Debited:</span>
                <span className="value">{formatCurrency(analytics.totals.debited)}</span>
              </div>
              <div className="analytics-item">
                <span className="label">Total Collected:</span>
                <span className="value collected">{formatCurrency(analytics.totals.collected)}</span>
              </div>
              <div className="analytics-item">
                <span className="label">Outstanding:</span>
                <span className="value outstanding">{formatCurrency(analytics.totals.outstanding)}</span>
              </div>
            </div>
          </div>

          <div className="analytics-card metrics">
            <h5>Performance Metrics</h5>
            <div className="analytics-items">
              <div className="analytics-item">
                <span className="label">Collection Rate:</span>
                <span className="value">{formatPercentage(analytics.metrics.collection_rate)}</span>
              </div>
              <div className="analytics-item">
                <span className="label">Recovery Efficiency:</span>
                <span className="value">{formatPercentage(analytics.metrics.recovery_efficiency)}</span>
              </div>
              <div className="analytics-item">
                <span className="label">Scheduling Rate:</span>
                <span className="value">{formatPercentage(analytics.metrics.scheduling_rate)}</span>
              </div>
              <div className="analytics-item">
                <span className="label">Debit Rate:</span>
                <span className="value">{formatPercentage(analytics.metrics.debit_rate)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderImposedFees = (imposedFees) => {
    const feeArray = Object.values(imposedFees);
    if (feeArray.length === 0) return <p className="no-data">No imposed fees</p>;

    const totalAmount = feeArray.reduce((sum, fee) => sum + fee.amount, 0);
    const totalStudents = feeArray.reduce((sum, fee) => sum + fee.student_count, 0);

    const pieData = {
      labels: feeArray.map(fee => fee.fee_type_head_name),
      datasets: [{
        data: feeArray.map(fee => fee.amount),
        backgroundColor: ['#7F56DA', '#6A47B8', '#9B7EE5', '#B39DEB', '#5A3D99', '#C5B2F0', '#8866CC', '#A688DD'],
        borderWidth: 0,
      }]
    };

    const chartOpts = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'right', labels: { font: { family: 'Monosans', size: 10 }, padding: 8, boxWidth: 10 } },
        tooltip: { callbacks: { label: (ctx) => `${ctx.label}: ${formatCurrency(ctx.parsed)}` } },
      },
    };

    return (
      <div className="compact-container">
        <div className="stats-row">
          <div className="stat-box">
            <span className="stat-label">Total</span>
            <span className="stat-value">{formatCurrency(totalAmount)}</span>
          </div>
          <div className="stat-box">
            <span className="stat-label">Students</span>
            <span className="stat-value">{totalStudents}</span>
          </div>
          <div className="stat-box">
            <span className="stat-label">Types</span>
            <span className="stat-value">{feeArray.length}</span>
          </div>
        </div>
        
        <div className="items-list">
          {feeArray.map((fee, idx) => (
            <div key={idx} className="item-row">
              <div className="item-info">
                <span className="item-name">{fee.fee_type_head_name}</span>
                <span className="item-badge">{fee.student_count} students</span>
              </div>
              <span className="item-amount">{formatCurrency(fee.amount)}</span>
            </div>
          ))}
        </div>

        <div className="compact-card full-width">
          <h5>Fee Distribution</h5>
          <div className="chart-box" style={{ height: '220px' }}>
            <Pie data={pieData} options={chartOpts} />
          </div>
        </div>
      </div>
    );
  };

  const renderScheduledFees = (scheduledFees) => {
    const feeArray = Object.values(scheduledFees);
    if (feeArray.length === 0) return <p className="no-data">No scheduled fees</p>;

    const totalAmount = feeArray.reduce((sum, fee) => sum + fee.amount, 0);
    const totalStudents = feeArray.reduce((sum, fee) => sum + fee.student_count, 0);

    const pieData = {
      labels: feeArray.map(fee => fee.fee_head_name),
      datasets: [{
        data: feeArray.map(fee => fee.amount),
        backgroundColor: ['#00875A', '#006644', '#36B37E', '#57D9A3', '#79F2C0', '#004D33', '#1F845A', '#2EA371'],
        borderWidth: 0,
      }]
    };

    const chartOpts = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'right', labels: { font: { family: 'Monosans', size: 10 }, padding: 8, boxWidth: 10 } },
        tooltip: { callbacks: { label: (ctx) => `${ctx.label}: ${formatCurrency(ctx.parsed)}` } },
      },
    };

    return (
      <div className="compact-container">
        <div className="stats-row">
          <div className="stat-box green">
            <span className="stat-label">Total</span>
            <span className="stat-value">{formatCurrency(totalAmount)}</span>
          </div>
          <div className="stat-box green">
            <span className="stat-label">Students</span>
            <span className="stat-value">{totalStudents}</span>
          </div>
          <div className="stat-box green">
            <span className="stat-label">Fees</span>
            <span className="stat-value">{feeArray.length}</span>
          </div>
        </div>
        
        <div className="items-list">
          {feeArray.map((fee, idx) => (
            <div key={idx} className="item-row green-item">
              <div className="item-info">
                <span className="item-name">{fee.fee_head_name}</span>
                <span className="item-badge">
                  {fee.student_count} students • Due {new Date(fee.due_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                </span>
              </div>
              <span className="item-amount">{formatCurrency(fee.amount)}</span>
            </div>
          ))}
        </div>

        <div className="compact-card full-width">
          <h5>Distribution</h5>
          <div className="chart-box" style={{ height: '220px' }}>
            <Pie data={pieData} options={chartOpts} />
          </div>
        </div>
      </div>
    );
  };

  const renderLedger = (ledger) => {
    const ledgerArray = Object.values(ledger);
    if (ledgerArray.length === 0) return <p className="no-data">No ledger entries</p>;

    const totalDebit = ledgerArray.reduce((sum, entry) => sum + entry.debit, 0);
    const totalCredit = ledgerArray.reduce((sum, entry) => sum + entry.credit, 0);
    const totalOutstanding = totalDebit - totalCredit;
    const collectionRate = totalDebit > 0 ? ((totalCredit / totalDebit) * 100) : 0;

    const pieData = {
      labels: ['Collected', 'Outstanding'],
      datasets: [{ data: [totalCredit, totalOutstanding > 0 ? totalOutstanding : 0], backgroundColor: ['#00875A', '#DE350B'], borderWidth: 0 }],
    };

    const chartOpts = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'bottom', labels: { font: { family: 'Monosans', size: 10 }, padding: 8, boxWidth: 10 } },
        tooltip: { callbacks: { label: (ctx) => `${ctx.label}: ${formatCurrency(ctx.parsed)}` } },
      },
    };

    return (
      <div className="compact-container">
        <div className="stats-row">
          <div className="stat-box red">
            <span className="stat-label">Debited</span>
            <span className="stat-value">{formatCurrency(totalDebit)}</span>
          </div>
          <div className="stat-box green">
            <span className="stat-label">Collected</span>
            <span className="stat-value">{formatCurrency(totalCredit)}</span>
          </div>
          <div className="stat-box red">
            <span className="stat-label">Outstanding</span>
            <span className="stat-value">{formatCurrency(totalOutstanding)}</span>
          </div>
          <div className="stat-box">
            <span className="stat-label">Rate</span>
            <span className="stat-value">{collectionRate.toFixed(1)}%</span>
          </div>
        </div>

        <div className="items-list">
          {ledgerArray.map((entry, idx) => {
            const balance = entry.debit - entry.credit;
            const isPaid = balance <= 0;
            return (
              <div key={idx} className={`item-row ${isPaid ? 'paid-item' : 'outstanding-item'}`}>
                <div className="item-info">
                  <span className="item-name">{entry.fee_head_name}</span>
                  <span className="item-badge">
                    {entry.student_count} students
                    {entry.due_date && ` • Due ${new Date(entry.due_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`}
                  </span>
                </div>
                <div className="item-amounts">
                  <span className="amount-item debit">₹{(entry.debit / 100000).toFixed(1)}L</span>
                  <span className="amount-item credit">₹{(entry.credit / 100000).toFixed(1)}L</span>
                  <span className={`amount-item balance ${isPaid ? 'paid' : 'outstanding'}`}>
                    {isPaid ? '✓ Paid' : formatCurrency(balance)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="compact-card full-width">
          <h5>Collection Progress • {collectionRate.toFixed(0)}% Complete</h5>
          <div className="chart-box" style={{ height: '200px' }}>
            <Pie data={pieData} options={chartOpts} />
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <SpinnerLoader/>
    );
  }

  return (
    <div className="course-fee-dashboard"  style={{
    height: "100vh",
    overflowY: "auto",
    overflowX: "hidden",
}}>
      <div className="dashboard-header">
        <h1>Course Fee Dashboard</h1>
        <p className="subtitle">Comprehensive view of imposed fees, scheduled fees, and ledger entries by course</p>
      </div>

      <div className="courses-container">
        {courses.length === 0 ? (
          <div className="no-data-message">
            <p>No course data available</p>
          </div>
        ) : (
          courses.map((course) => (
            <div key={course.course_id} className="course-card">
              <div 
                className="course-header"
                onClick={() => toggleCourse(course.course_id)}
              >
                <div className="course-info">
                  <h3>{course.course_name}</h3>
                  <span className="student-count">
                    {course.total_students} Students
                  </span>
                </div>
                <div className="course-summary">
                  <div className="summary-item">
                    <span className="label">Imposed:</span>
                    <span className="value">
                      {formatCurrency(
                        Object.values(course.imposed_fee).reduce((sum, fee) => sum + fee.amount, 0)
                      )}
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="label">Scheduled:</span>
                    <span className="value">
                      {formatCurrency(
                        Object.values(course.scheduled_fee).reduce((sum, fee) => sum + fee.amount, 0)
                      )}
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="label">Collected:</span>
                    <span className="value collected">
                      {formatCurrency(
                        Object.values(course.ledger).reduce((sum, entry) => sum + entry.credit, 0)
                      )}
                    </span>
                  </div>
                </div>
                <button className="expand-btn">
                  {expandedCourse === course.course_id ? '−' : '+'}
                </button>
              </div>

              {expandedCourse === course.course_id && (
                <div className="course-details">
                  {renderAnalytics(course.analytics)}

                  <div className="tabs-container-course">
                    <div className="tabs">
                      <button
                        className={`tab ${activeTab === 'imposed' ? 'active' : ''}`}
                        onClick={() => setActiveTab('imposed')}
                      >
                        Imposed Fees ({Object.keys(course.imposed_fee).length})
                      </button>
                      <button
                        className={`tab ${activeTab === 'scheduled' ? 'active' : ''}`}
                        onClick={() => setActiveTab('scheduled')}
                      >
                        Scheduled Fees ({Object.keys(course.scheduled_fee).length})
                      </button>
                      <button
                        className={`tab ${activeTab === 'ledger' ? 'active' : ''}`}
                        onClick={() => setActiveTab('ledger')}
                      >
                        Ledger Entries ({Object.keys(course.ledger).length})
                      </button>
                    </div>

                    <div className="tab-content">
                      {activeTab === 'imposed' && renderImposedFees(course.imposed_fee)}
                      {activeTab === 'scheduled' && renderScheduledFees(course.scheduled_fee)}
                      {activeTab === 'ledger' && renderLedger(course.ledger)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};
